import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import { useI18n } from "@/lib/i18n/useI18n";
import useRequest from "@/lib/request/useRequest";
import { roundTo } from "@/lib/utils/numbers";
import { sortArrayByKey } from "@/lib/utils/sorting";
import {
    CropWaterUse,
    WaterRiskZones,
    ProbeSimulation,
    WaterReceived,
    WaterRisks,
} from "@/types/water";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import {
    Bar,
    BarChart,
    ResponsiveContainer,
    YAxis,
    XAxis,
    Tooltip,
    Legend,
    ComposedChart,
    Line,
    Area,
    LineChart,
} from "recharts";
import { scaleTime } from "d3-scale";
import { useTranslation } from "react-i18next";
import useResizeObserver from "@/lib/utils/useResizeObserver";
import { Chip, Skeleton, Switch } from "@heroui/react";
import useAuth from "@/lib/auth/useAuth";
import { Resource } from "@/types/user";
import { useFormContext } from "react-hook-form";

function mmToIn(mm: number) {
    return mm * 0.0393701;
}

function WaterCharts() {
    const { useGetRequest } = useRequest();
    const { fieldId } = useFieldDashboard() ?? {};
    const { formatDateMMDDYYYY, formatUnit, isImperial } = useI18n();
    const { t } = useTranslation();
    const fieldSoilWaterContentQuery = useGetRequest<WaterRiskZones>({
        url: `fields/${fieldId}/water_risk_zones/`,
    });

    const f = useFormContext();
    const [startDate, endDate] = f.watch(["startDate", "endDate"]);
    const fieldSoilWaterContentData = useMemo(() => {
        return sortArrayByKey(
            fieldSoilWaterContentQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(startDate) &&
                        dayjs(d.time).isSameOrBefore(endDate),
                )
                .map((d) => ({
                    ...d,
                    time: dayjs(d.time).toDate().getTime(),
                    range: [d.swc_sig_under, d.swc_sig_over],
                    green: [d.rtsml_b, d.rtsml_t],
                    red: [0, d.rtsml_b],
                    blue: [d.rtsml_t, d.fhc],
                })) ?? [],
            "time",
        );
    }, [fieldSoilWaterContentQuery.data?.data, startDate, endDate]);

    const waterReceivedQuery = useGetRequest<WaterReceived>({
        url: `fields/${fieldId}/water_received/`,
    });

    const waterReceivedData = useMemo(() => {
        return sortArrayByKey(
            waterReceivedQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(startDate) &&
                        dayjs(d.time).isSameOrBefore(endDate),
                )
                .map((d) => ({
                    ...d,
                    time: dayjs(d.time).toDate().getTime(),
                })) ?? [],
            "time",
        );
    }, [waterReceivedQuery.data?.data, startDate, endDate]);

    const totalPrecipitation = useMemo(() => {
        return roundTo(
            waterReceivedData.reduce(
                (acc, curr) => acc + curr.precipitation,
                0,
            ),
            2,
        );
    }, [waterReceivedData]);
    const totalIrrigation = useMemo(() => {
        return roundTo(
            waterReceivedData.reduce((acc, curr) => acc + curr.irrigation, 0),
            2,
        );
    }, [waterReceivedData]);

    const probeSimulationQuery = useGetRequest<ProbeSimulation>({
        url: `fields/${fieldId}/probe_simulation/`,
    });

    const probeSimulationData = useMemo(() => {
        return sortArrayByKey(
            probeSimulationQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(startDate) &&
                        dayjs(d.time).isSameOrBefore(endDate),
                )
                .map((d) => ({
                    ...d,
                    time: dayjs(d.time).toDate().getTime(),
                })) ?? [],
            "time",
        );
    }, [probeSimulationQuery.data?.data, startDate, endDate]);

    const probeSimulationColorKey = useMemo(() => {
        return {
            swc_005cm: {
                color: "#4F8DFD",
                name: `Soil Water Content ${isImperial ? roundTo(mmToIn(50), 1) : 50} in`,
            },
            swc_015cm: {
                color: "#42C68E",
                name: `Soil Water Content ${isImperial ? roundTo(mmToIn(150), 1) : 150} in`,
            },
            swc_030cm: {
                color: "#FFD16F",
                name: `Soil Water Content ${isImperial ? roundTo(mmToIn(300), 1) : 300} in`,
            },
            swc_060cm: {
                color: "#F96C77",
                name: `Soil Water Content ${isImperial ? roundTo(mmToIn(600), 1) : 600} in`,
            },
        };
    }, [isImperial]);

    const cropWaterUseQuery = useGetRequest<CropWaterUse>({
        url: `fields/${fieldId}/crop_water_use/`,
    });

    const cropWaterUseData = useMemo(() => {
        return sortArrayByKey(
            cropWaterUseQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(startDate) &&
                        dayjs(d.time).isSameOrBefore(endDate),
                )
                .map((d) => ({
                    ...d,
                    range: [d.et_under, d.et_over],
                    replacement: isImperial ? 0.3 : 7.62,
                    time: dayjs(d.time).toDate().getTime(),
                })) ?? [],
            "time",
        );
    }, [cropWaterUseQuery.data?.data, isImperial, startDate, endDate]);

    const { isAtLeastDetail } = useAuth();

    const waterRisksQuery = useGetRequest<WaterRisks>({
        url: `fields/${fieldId}/water_risks/`,
        enabled: isAtLeastDetail(Resource.IRRIGATION),
    });

    const waterRisksData = useMemo(() => {
        return sortArrayByKey(
            waterRisksQuery.data?.data
                ?.filter(
                    (d) =>
                        dayjs(d.time).isSameOrAfter(startDate) &&
                        dayjs(d.time).isSameOrBefore(endDate),
                )
                .map((d) => ({
                    ...d,
                    time: dayjs(d.time).toDate().getTime(),
                })) ?? [],
            "time",
        );
    }, [waterRisksQuery.data?.data, startDate, endDate]);

    const maxValue = Math.max(
        Math.ceil(
            Math.max(...waterReceivedData.map((d) => d.time as number)) / 2,
        ) * 2,
    );
    const minValue = Math.min(
        ...waterReceivedData.map((d) => d.time as number),
    );
    const timeScale = scaleTime().domain([minValue, maxValue]);

    const domain = timeScale.domain().map((d) => d.getTime());
    const ticks = timeScale.ticks(5).map((d) => d.getTime());

    const [minSoilWaterContent, maxSoilWaterContent] = useMemo(() => {
        return [
            Math.min(
                ...fieldSoilWaterContentData
                    .filter((d) => d.swc_sig_under !== null)
                    .map((d) => d.swc_sig_under as number),
            ),
            Math.max(
                ...fieldSoilWaterContentData
                    .filter((d) => d.swc_sig_over !== null)
                    .map((d) => d.swc_sig_over as number),
            ),
        ];
    }, [fieldSoilWaterContentData]);

    const { elementRef, isResizing } = useResizeObserver(250);

    const [showSWCRanges, setShowSWCRanges] = useState(false);

    return (
        <div className="w-full h-[100rem]  " ref={elementRef}>
            {isResizing ? (
                <Skeleton className="h-full w-full" />
            ) : (
                <div className="flex flex-col gap-8">
                    <div>
                        <div className="flex flex-row items-center gap-2 justify-end">
                            <Chip
                                color="primary"
                                size="sm"
                                classNames={{
                                    base: "bg-[#8884d8] text-white",
                                }}
                            >
                                Total Irrigation: {totalIrrigation}{" "}
                                {formatUnit("in", "mm")}
                            </Chip>
                            <Chip
                                color="primary"
                                size="sm"
                                classNames={{
                                    base: "bg-[#82ca9d] text-white",
                                }}
                            >
                                Total Precipitation: {totalPrecipitation}{" "}
                                {formatUnit("in", "mm")}
                            </Chip>
                        </div>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={waterReceivedData} syncId="water">
                                <XAxis
                                    dataKey="time"
                                    scale="linear"
                                    type="number"
                                    spacing={50}
                                    angle={-8}
                                    fontSize={12}
                                    tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                    domain={domain}
                                    ticks={ticks}
                                />
                                <YAxis
                                    fontSize={12}
                                    label={{
                                        value: t("chartLabels.waterReceived", {
                                            ns: "insights",
                                        }),
                                        angle: -90,
                                        dx: -20,
                                    }}
                                />
                                <Bar
                                    dataKey="irrigation"
                                    fill="#8884d8"
                                    barSize={10}
                                    name={t("chartLabels.irrigation", {
                                        ns: "insights",
                                    })}
                                />
                                <Bar
                                    dataKey="precipitation"
                                    fill="#82ca9d"
                                    barSize={10}
                                    name={t("chartLabels.precipitation", {
                                        ns: "insights",
                                    })}
                                />
                                <Tooltip
                                    labelFormatter={(t) =>
                                        formatDateMMDDYYYY(t)
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                        name,
                                    ]}
                                />
                                <Legend
                                    verticalAlign="top"
                                    height={36}
                                    fontSize={12}
                                    wrapperStyle={{ fontSize: "12px" }}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div>
                        <div className="flex flex-row items-center gap-2 justify-end">
                            <div className="text-sm">Uncertainty Threshold</div>
                            <Switch
                                size="sm"
                                aria-label="Uncertainty Threshold"
                                isSelected={showSWCRanges}
                                onValueChange={setShowSWCRanges}
                            />
                        </div>
                        <ResponsiveContainer width="100%" height={250}>
                            <ComposedChart
                                data={fieldSoilWaterContentData}
                                syncId="water"
                            >
                                <XAxis
                                    dataKey="time"
                                    scale="linear"
                                    type="number"
                                    spacing={50}
                                    angle={-8}
                                    fontSize={12}
                                    tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                    domain={domain}
                                    ticks={ticks}
                                />
                                <YAxis
                                    fontSize={12}
                                    label={{
                                        value: t(
                                            "chartLabels.fieldSoilWaterContent",
                                            { ns: "insights" },
                                        ),
                                        angle: -90,
                                        dx: -20,
                                    }}
                                    domain={[
                                        Math.floor(minSoilWaterContent),
                                        Math.ceil(maxSoilWaterContent),
                                    ]}
                                    allowDataOverflow
                                    type="number"
                                    scale="linear"
                                />
                                <Area
                                    dataKey="red"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="red"
                                    fillOpacity={0.1}
                                    name={t("chartLabels.nearingDepletion", {
                                        ns: "insights",
                                    })}
                                    tooltipType="none"
                                    stroke="red"
                                    legendType="rect"
                                />
                                <Area
                                    dataKey="blue"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="blue"
                                    fillOpacity={0.1}
                                    name={t("chartLabels.nearingSaturation", {
                                        ns: "insights",
                                    })}
                                    tooltipType="none"
                                    stroke="blue"
                                    legendType="rect"
                                />
                                <Area
                                    dataKey="green"
                                    dot={false}
                                    strokeWidth={0}
                                    fill="green"
                                    fillOpacity={0.1}
                                    name={t("chartLabels.optimal", {
                                        ns: "insights",
                                    })}
                                    tooltipType="none"
                                    stroke="green"
                                    legendType="rect"
                                />
                                {showSWCRanges && (
                                    <>
                                        <Area
                                            dataKey="range"
                                            dot={false}
                                            strokeWidth={0}
                                            fill="black"
                                            fillOpacity={0.2}
                                            tooltipType="none"
                                            legendType="none"
                                        />
                                    </>
                                )}

                                <Line
                                    dataKey="swc_est"
                                    stroke="black"
                                    dot={false}
                                    strokeWidth={3}
                                    name={t("chartLabels.soilWaterContent", {
                                        ns: "insights",
                                    })}
                                />
                                <Tooltip
                                    labelFormatter={(t) =>
                                        formatDateMMDDYYYY(t)
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                        name,
                                    ]}
                                />
                                <Legend
                                    verticalAlign="top"
                                    height={36}
                                    fontSize={12}
                                    wrapperStyle={{ fontSize: "12px" }}
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </div>
                    <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={probeSimulationData} syncId="water">
                            <XAxis
                                dataKey="time"
                                scale="linear"
                                type="number"
                                spacing={50}
                                angle={-8}
                                fontSize={12}
                                tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                domain={domain}
                                ticks={ticks}
                            />
                            <YAxis
                                fontSize={12}
                                label={{
                                    value: `${t("chartLabels.probeSimulation", {
                                        ns: "insights",
                                    })} ${formatUnit("in", "mm")}`,
                                    angle: -90,
                                    dx: -20,
                                }}
                            />
                            {Object.entries(probeSimulationColorKey).map(
                                ([key, value]) => (
                                    <Line
                                        dataKey={key}
                                        stroke={value.color}
                                        dot={false}
                                        strokeWidth={1}
                                        name={value.name}
                                    />
                                ),
                            )}

                            <Tooltip
                                labelFormatter={(t) => formatDateMMDDYYYY(t)}
                                formatter={(value, name) => [
                                    `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                    name,
                                ]}
                            />
                            <Legend
                                verticalAlign="top"
                                height={36}
                                fontSize={12}
                                wrapperStyle={{ fontSize: "12px" }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                    <ResponsiveContainer width="100%" height={250}>
                        <LineChart data={cropWaterUseData} syncId="water">
                            <XAxis
                                dataKey="time"
                                scale="linear"
                                type="number"
                                spacing={50}
                                angle={-8}
                                fontSize={12}
                                tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                domain={domain}
                                ticks={ticks}
                            />
                            <YAxis
                                fontSize={12}
                                label={{
                                    value: t("chartLabels.cropWaterUse", {
                                        ns: "insights",
                                    }),
                                    angle: -90,
                                    dx: -20,
                                }}
                            />
                            <Line
                                dataKey="et_estimate"
                                stroke="orange"
                                dot={false}
                                strokeWidth={1}
                                name={t("chartLabels.etEstimate", {
                                    ns: "insights",
                                })}
                            />
                            <Line
                                dataKey="range"
                                stroke="orange"
                                dot={false}
                                strokeWidth={1}
                                opacity={0.5}
                                legendType="none"
                                tooltipType="none"
                            />
                            <Line
                                dataKey="replacement"
                                stroke="black"
                                strokeDasharray="5 5"
                                dot={false}
                                strokeWidth={1}
                                tooltipType="none"
                                name={t("chartLabels.replacement", {
                                    ns: "insights",
                                })}
                            />
                            <Tooltip
                                labelFormatter={(t) => formatDateMMDDYYYY(t)}
                                formatter={(value, name) => [
                                    `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                    name,
                                ]}
                            />
                            <Legend
                                verticalAlign="top"
                                height={36}
                                fontSize={12}
                                wrapperStyle={{ fontSize: "12px" }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                    {isAtLeastDetail(Resource.IRRIGATION) && (
                        <ResponsiveContainer width="100%" height={250}>
                            <LineChart data={waterRisksData} syncId="water">
                                <XAxis
                                    dataKey="time"
                                    scale="linear"
                                    type="number"
                                    spacing={50}
                                    angle={-8}
                                    fontSize={12}
                                    tickFormatter={(t) => formatDateMMDDYYYY(t)}
                                    domain={domain}
                                    ticks={ticks}
                                />
                                <YAxis
                                    fontSize={12}
                                    label={{
                                        value: "Risk %",
                                        angle: -90,
                                        dx: -20,
                                    }}
                                    domain={[0, 100]}
                                    allowDataOverflow
                                    type="number"
                                    scale="linear"
                                />
                                <Line
                                    dataKey="leach_risk"
                                    stroke="blue"
                                    dot={false}
                                    strokeWidth={1}
                                    name={t("chartLabels.leachingRisk", {
                                        ns: "insights",
                                    })}
                                    legendType="line"
                                />
                                <Line
                                    dataKey="water_risk"
                                    stroke="orange"
                                    dot={false}
                                    strokeWidth={1}
                                    name={t("chartLabels.waterRisk", {
                                        ns: "insights",
                                    })}
                                    legendType="line"
                                />
                                <Tooltip
                                    labelFormatter={(t) =>
                                        formatDateMMDDYYYY(t)
                                    }
                                    formatter={(value, name) => [
                                        `${roundTo(value as number, 1)} ${formatUnit("in", "mm")}`,
                                        name,
                                    ]}
                                />
                                <Legend
                                    verticalAlign="top"
                                    height={36}
                                    fontSize={12}
                                    wrapperStyle={{ fontSize: "12px" }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>
            )}
        </div>
    );
}

export default WaterCharts;
